//angular imports
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
  Signal,
  untracked,
  WritableSignal,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

//local imports
import {
  ProgramModel,
  ProjectModel,
  SpaDto,
} from "@app/catalog";
import { FormValueModel } from "@app/shared";

/**
 * @summary A model to hold the state for the current details form.
 */
export class ProjectDetails {
  /**
   * @summary The current entity name
   */
  public name: FormValueModel<string>;

  /**
   * @summary The current entity description
   */
  public description: FormValueModel<string>;

  /**
   * @summary The current selected program
   */
  public program: FormValueModel<string>;

  /**
   * @summary The externally-sourced CAP number (free text, unvalidated)
   */
  public capNumber: FormValueModel<string>;

  /**
   * @summary The selected SPA file, if any (a new file being uploaded this session).
   */
  public spaFile: WritableSignal<File | null> = signal<File | null>(null);

  /**
   * @summary The project's already-uploaded SPA file, if any. Read-only pre-population - selecting a new spaFile takes precedence over this.
   * @description The API returns only an id and a name, no URL, so this is displayed as plain text rather than a link.
   */
  public existingSpa: WritableSignal<SpaDto | null> = signal<SpaDto | null>(null);

  /**
   * @summary Indicates if the form has changes.
   */
  public hasChanges: Signal<boolean>;

  /**
   * @summary Indicates if the form has validation issues
   */
  public isValid: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * @summary Indicates if the form is awaiting validation
   */
  public isPending: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * @summary Creates a new Details instance using the provided entity to prepopulate it.
   * @description This is used for the edit form
   * @param entity: the model for the entity being changed
   * @returns An instance of a details model for tracking changes
   */
  public static fromProject(programId: string, entity: ProjectModel) {
    let _this = new ProjectDetails();

    _this.name = FormValueModel.create(entity.name());
    _this.description = FormValueModel.create(entity.description());
    _this.program = FormValueModel.create(programId);
    _this.capNumber = FormValueModel.create(entity.projectCap());
    _this.existingSpa = signal<SpaDto | null>(entity.spa());

    _this.hasChanges = FormValueModel.hasChanges(
      [
        _this.program,
        _this.name,
        _this.description,
        _this.capNumber,
      ]
    );

    return _this;
  }

  /**
   * @summary Creates a new Details instance with no data.
   * @description This is used for the add form
   * @returns An instance of a details model for tracking changes
   */
  public static empty() {
    let _this = new ProjectDetails();

    _this.name = FormValueModel.create<string>(undefined);
    _this.description = FormValueModel.create<string>(undefined);
    _this.program = FormValueModel.create<string>(undefined);
    _this.capNumber = FormValueModel.create<string>(undefined);

    _this.hasChanges = FormValueModel.hasChanges(
      [
        _this.name,
        _this.description,
        _this.program,
        _this.capNumber,
      ]
    );

    return _this;
  }
}

@Component({
  selector: "project-details",
  templateUrl: "./project-details.component.html",
  styleUrl: "./project-details.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProjectDetailsComponent {
  private readonly destroying$ = new Subject<void>();

  public project = input.required<ProjectDetails>();
  public programs = input.required<ProgramModel[]>();

  public form: Signal<FormGroup> = computed(() => {
    let entity = this.project();

    let formParts = {
      name: [
        untracked(() => entity?.name.value() || ""),
        {
          validators: [
            Validators.required,
            Validators.pattern("^(?!\\s+$).+"), //Cannot be whitespace
          ],
        },
      ],
      description: [untracked( () => entity?.description.value() || '' )],
      program: [untracked( () => entity?.program.value() || '' ), {
        validators: [
          Validators.required,
          Validators.pattern('^(?!\\s+$).+') //Ca  nnot be whitespace
        ]
      }],
      capNumber: [untracked(() => entity?.capNumber.value() || '')],
    };

    let form = this.formBuilder.group(formParts);

    // Subscribe to form value changes, emit events.
    form.valueChanges.pipe(takeUntil(this.destroying$)).subscribe((_) => {
      let rawValue = form.getRawValue();

      let details = this.project();

      details.name.value.set(rawValue.name);
      details.description.value.set(rawValue.description);
      details.program.value.set(rawValue.program);
      details.capNumber.value.set(rawValue.capNumber);
    });

    //If the form changes, pass the validation status back into the model
    //
    //NB: Setting the signal in a computed signal can cause problems if the value is set before the signal is complete.
    //    In this case it doesn't occur because the validation is changed by the async validator configured on the form, it has a
    //    debounceTime of 500ms so this is enough to allow this computed signal to complete.
    form.statusChanges.pipe(takeUntil(this.destroying$)).subscribe((_) => {
      let details = this.project();

      if (form.pending) {
        details.isPending.set(true);
        return;
      }

      details.isPending.set(false);
      details.isValid.set(form.valid);
    });

    return form;
  });

  constructor(private formBuilder: FormBuilder) {}

  public onSpaFileSelected(event: Event): void {
    let input = event.target as HTMLInputElement;
    let file = input.files?.[0] ?? null;
    this.project().spaFile.set(file);
  }

  ngOnDestroy(): void {
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }
}
